import AccountRoles from "@Enums/AccountRoles";
import useGlobalContext from "@hooks/common/general/use-global-context";
import { AccountProductRecord } from "@Shared/schemas/accounts/accountSchemas";
import { useCallback } from "react";

export interface ExpiringProduct {
  accountProduct: AccountProductRecord;
  productName: string;
  expiryDate: Date;
  stripeSubscriptionId?: string | null;
}

export interface ExpiringProductsResult {
  earliestProduct: ExpiringProduct;
  count: number;
  allExpiringProducts: ExpiringProduct[];
}

interface DemoAccountInfo {
  isDemo: boolean;
  getExpiringProducts: () => ExpiringProductsResult | null;
}

function useDemoAccount(): DemoAccountInfo {
  const { globalState } = useGlobalContext();
  const isDemoAccount = Boolean(globalState.account.data?.is_demo);
  const hasDemoRole = globalState.account.user_role === AccountRoles.DEMO;
  const isDemo = isDemoAccount || hasDemoRole;

  const getExpiringProducts = useCallback(() => {
    const products = globalState.account.data?.products || [];
    const productRecords = globalState.account.data?.product_records || [];
    
    const activeProductsWithEndDate = products.filter(
      (product) => product.is_active && product.end_at
    );

    if (activeProductsWithEndDate.length === 0) return null;

    const expiringProducts: ExpiringProduct[] = activeProductsWithEndDate.map((product) => {
      const productRecord = productRecords.find((record) => record.id === product.product_id);
      return {
        accountProduct: product,
        productName: productRecord?.name || "Unknown Product",
        expiryDate: new Date(product.end_at!),
        stripeSubscriptionId: product.stripe_subscription_id,
      };
    });

    // Sort by expiry date (earliest first)
    expiringProducts.sort((a, b) => a.expiryDate.getTime() - b.expiryDate.getTime());

    return {
      earliestProduct: expiringProducts[0],
      count: expiringProducts.length,
      allExpiringProducts: expiringProducts,
    };
  }, [
    globalState.account.data?.products,
    globalState.account.data?.product_records,
  ]);

  return { isDemo, getExpiringProducts };
}

export default useDemoAccount;

